import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

/**
 * Drop-in `useState` replacement whose value survives a remount within the
 * browser tab (sessionStorage, JSON-serialized).
 *
 * Built for list-page filters: opening a row's detail page unmounts the list,
 * so plain state would force the user to re-filter after every "back". Session
 * scope keeps the restored state fresh — a new tab or next-day visit starts
 * clean, unlike localStorage-backed preferences (see usePersistentToggle).
 *
 * Storage failures (private mode, quota) fall back to in-memory state.
 */
export function useStoredState<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Best-effort persistence — the state still works for this mount.
    }
  }, [key, value]);

  return [value, setValue];
}
